import React, { useCallback, useEffect } from 'react';

import { SERVER_ERROR_MODAL_TEXT, SERVER_ERROR_MODAL_TITLE } from '@/common/constants';
import ROUTES from '@/common/routes';
import { WarnModal } from '@/components/elements';
import useHistory from '@/modules/core/hooks/useHistory';

import { CreatePayoutDefinitionModal } from './components';
import { useCreatePayoutDefinitionForm, useCreatePayoutDefinitionSubmit } from './hooks';
import { useCreatePayoutDefinitionQuery, usePayoutDefinitionsLiteQuery } from './queries';

interface Props {
  visible: boolean;
  onClose: () => void;
}

const CreatePayoutDefinitionModalContainer: React.FC<Props> = ({ visible, onClose }) => {
  const history = useHistory();
  const { methods } = useCreatePayoutDefinitionForm();
  const { fieldOptions } = usePayoutDefinitionsLiteQuery();
  const createMutation = useCreatePayoutDefinitionQuery();

  const handleClose = useCallback(() => {
    methods.reset();
    onClose();
  }, [methods, onClose]);

  const handleSuccess = useCallback(
    (id: string) => {
      history.push(ROUTES.PAYOUT_DEFINITION_EDIT_BY_ID(id));
    },
    [history]
  );

  const { handleSubmit, isLoading, isServerError, setServerError } = useCreatePayoutDefinitionSubmit({
    mutateAsync: createMutation.mutateAsync,
    isLoading: !!createMutation.isLoading,
    onSuccess: handleSuccess,
    onClose: handleClose,
  });

  useEffect(() => {
    if (visible) {
      methods.reset({ name: '', copyFromId: '' });
    }
  }, [visible, methods]);

  const onSubmit = methods.handleSubmit(handleSubmit);

  return (
    <>
      <CreatePayoutDefinitionModal
        visible={visible}
        methods={methods}
        payoutDefinitionOptions={fieldOptions}
        isLoading={isLoading}
        onClose={handleClose}
        onSubmit={onSubmit}
      />
      <WarnModal
        visible={isServerError}
        onSetVisible={() => setServerError(false)}
        header={SERVER_ERROR_MODAL_TITLE}
        helptext={SERVER_ERROR_MODAL_TEXT}
        buttonText="Close"
        onSubmit={onSubmit}
        submitButtonText="Try Again"
      />
    </>
  );
};

export default CreatePayoutDefinitionModalContainer;
